import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import MasonryGallery from "../components/MasonryGallery";
import Seo from "../components/Seo";

const META = {
  musica:   { name: "Música",   tagline: "Ayudar a desarrollar la voz del artista y contribuir a la conexión con su público.", accent: "var(--magenta)" },
  eventos:  { name: "Eventos",  tagline: "Cultura e industrias creativas. Encuentros donde gente que crea coincide.",          accent: "var(--tungsten)" },
  viajes:   { name: "Viajes",   tagline: "Tender puentes. Establecer escenarios para el encuentro entre la diferencia.",       accent: "var(--cyan)" },
  retratos: { name: "Retratos", tagline: "El rostro como territorio. Gesto, intención, presencia.",                            accent: "var(--rose)" },
};

export default function Gallery() {
  const { categoria } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!META[categoria]) {
      navigate("/", { replace: true });
      return;
    }
    setLoading(true);
    api.get(`/projects?category=${categoria}`)
      .then((r) => setProjects(r.data))
      .finally(() => setLoading(false));
  }, [categoria, navigate]);

  const meta = META[categoria] || META.musica;
  const tabs = useMemo(() => Object.entries(META), []);

  return (
    <div data-testid={`gallery-page-${categoria}`} className="pt-[18vh] sm:pt-[22vh] pb-[10vh]">
      <Seo
        title={`${meta.name} — Serie fotográfica`}
        description={`${meta.tagline} Serie ${meta.name.toLowerCase()} por Emmanuel Upegui. ${projects.length || "Varios"} proyectos.`}
        path={`/galeria/${categoria}`}
        keywords={`fotografía ${meta.name.toLowerCase()}, ${meta.name.toLowerCase()} Emmanuel Upegui, serie fotográfica`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "name": `${meta.name} — Emmanuel Upegui`,
              "url": `https://www.emmanuelupegui.com/galeria/${categoria}`,
              "description": meta.tagline,
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://www.emmanuelupegui.com/" },
                { "@type": "ListItem", "position": 2, "name": meta.name, "item": `https://www.emmanuelupegui.com/galeria/${categoria}` },
              ],
            },
          ],
        }}
      />
      <header className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw]">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-[11px] tracking-[0.4em] uppercase font-display"
          style={{ color: meta.accent }}
        >
          Serie · {projects.length} proyectos
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.2,0.8,0.2,1] }}
          className="font-display uppercase font-bold tracking-tighter leading-[0.85] text-[18vw] sm:text-[14vw] lg:text-[11vw] mt-3"
        >
          {meta.name}
        </motion.h1>
        <p className="font-serif-accent text-xl sm:text-2xl text-white/70 mt-4">
          {meta.tagline}
        </p>

        <div className="mt-12 flex flex-wrap gap-2 border-t border-white/5 pt-8">
          {tabs.map(([slug, m]) => (
            <Link
              key={slug}
              to={`/galeria/${slug}`}
              data-testid={`gallery-filter-${slug}`}
              className={`px-5 py-2.5 text-[11px] tracking-[0.3em] uppercase font-display border transition-colors ${
                slug === categoria
                  ? "border-white/40 text-white"
                  : "border-white/10 text-white/50 hover:border-white/30 hover:text-white"
              }`}
            >
              {m.name}
            </Link>
          ))}
        </div>
      </header>

      <section className="max-w-[1500px] mx-auto px-6 sm:px-12 md:pl-[10vw] lg:pl-[8vw] mt-16 sm:mt-20 pb-24">
        {loading ? (
          <div data-testid="loading" className="text-white/40 text-sm tracking-widest uppercase py-24">Cargando…</div>
        ) : (
          <MasonryGallery items={projects} testId={`masonry-${categoria}`} />
        )}
      </section>
    </div>
  );
}
